/**
 * MLBPredictionsWidget
 * Displays MLB game predictions from the forecaster API.
 * CSS prefix: pred-
 * Created: 2026-04-06
 *
 * API: https://abet-mlb-forecaster-api.abetco.workers.dev/predictions/today
 * Auto-refreshes every 5 minutes.
 */

import { useState, useEffect, useCallback } from 'react'
import { findTeam } from '../lib/teamLogos'
import './MLBPredictionsWidget.css'

const API_URL = 'https://abet-mlb-forecaster-api.abetco.workers.dev/predictions/today'
const REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

function formatGameTime(isoString) {
  if (!isoString) return ''
  try {
    const d = new Date(isoString)
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return isoString
  }
}

function getConsensusInfo(game) {
  // consensus field or derive from model_count / agreement
  if (game.consensus) return game.consensus
  if (game.models_agreeing != null && game.model_count != null) {
    const ratio = game.models_agreeing / game.model_count
    if (ratio >= 0.8) return 'strong'
    if (ratio >= 0.6) return 'moderate'
    return 'split'
  }
  return null
}

function ConsensusTag({ consensus }) {
  if (!consensus) return null
  const labels = { strong: 'Strong', moderate: 'Moderate', split: 'Split' }
  return (
    <span className={`pred-consensus pred-consensus-${consensus}`}>
      {consensus === 'strong' && '\u2705'}
      {consensus === 'moderate' && '\u26A0\uFE0F'}
      {consensus === 'split' && '\u{1F504}'}
      {labels[consensus] || consensus}
    </span>
  )
}

function GameCard({ game }) {
  const awayTeam = findTeam(game.away_team || game.awayTeam)
  const homeTeam = findTeam(game.home_team || game.homeTeam)

  const awayName = awayTeam?.short || game.away_team || game.awayTeam || 'Away'
  const homeName = homeTeam?.short || game.home_team || game.homeTeam || 'Home'

  const awayProb = game.away_win_prob ?? game.awayWinProb ?? 0
  const homeProb = game.home_win_prob ?? game.homeWinProb ?? 0
  const awayPct = Math.round(awayProb * 100)
  const homePct = Math.round(homeProb * 100)

  const awayIsWinner = awayProb > homeProb
  const homeIsWinner = homeProb > awayProb

  const awayPitcher = game.away_pitcher || game.awayPitcher
  const homePitcher = game.home_pitcher || game.homePitcher

  const consensus = getConsensusInfo(game)
  const gameTime = formatGameTime(game.game_time || game.gameTime || game.commence_time)
  const modelName = game.model || game.model_name || null

  return (
    <div className="pred-card">
      <div className="pred-card-header">
        <span className="pred-game-time">
          {'\u{1F551}'} {gameTime || 'TBD'}
        </span>
        <ConsensusTag consensus={consensus} />
      </div>

      <div className="pred-matchup">
        <div className="pred-team pred-team-away">
          {awayTeam?.logo && (
            <img
              className="pred-team-logo"
              src={awayTeam.logo}
              alt={awayName}
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}
          <div className="pred-team-info">
            <span className={`pred-team-name ${awayIsWinner ? 'pred-winner' : ''}`}>
              {awayName}
            </span>
            {awayPitcher && (
              <span className="pred-team-pitcher">{awayPitcher}</span>
            )}
          </div>
        </div>

        <span className="pred-vs">@</span>

        <div className="pred-team pred-team-home">
          {homeTeam?.logo && (
            <img
              className="pred-team-logo"
              src={homeTeam.logo}
              alt={homeName}
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}
          <div className="pred-team-info">
            <span className={`pred-team-name ${homeIsWinner ? 'pred-winner' : ''}`}>
              {homeName}
            </span>
            {homePitcher && (
              <span className="pred-team-pitcher">{homePitcher}</span>
            )}
          </div>
        </div>
      </div>

      <div className="pred-prob-section">
        <div className="pred-prob-labels">
          <span className={`pred-prob-value ${awayIsWinner ? 'pred-prob-high' : 'pred-prob-low'}`}>
            {awayPct}%
          </span>
          <span className={`pred-prob-value ${homeIsWinner ? 'pred-prob-high' : 'pred-prob-low'}`}>
            {homePct}%
          </span>
        </div>
        <div className="pred-prob-bar-track">
          <div
            className="pred-prob-bar-fill-away"
            style={{ width: `${awayPct}%` }}
          />
          <div
            className="pred-prob-bar-fill-home"
            style={{ width: `${homePct}%` }}
          />
        </div>
      </div>

      {modelName && (
        <div className="pred-card-footer">
          <span className="pred-model-tag">{modelName}</span>
          {game.edge != null && (
            <span>Edge: {(game.edge * 100).toFixed(1)}%</span>
          )}
        </div>
      )}
    </div>
  )
}

export default function MLBPredictionsWidget() {
  const [predictions, setPredictions] = useState(null)
  const [modelStatus, setModelStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)

  const fetchPredictions = useCallback(async () => {
    try {
      setError(null)
      const res = await fetch(API_URL)

      if (!res.ok) {
        // API not deployed yet or no data — show placeholder
        if (res.status === 404 || res.status === 503) {
          setPredictions([])
          setModelStatus(null)
          setLoading(false)
          setLastFetch(new Date())
          return
        }
        throw new Error(`API returned ${res.status}`)
      }

      const data = await res.json()

      // API may return { predictions: [...], model_status: {...} } or just an array
      if (Array.isArray(data)) {
        setPredictions(data)
        setModelStatus(null)
      } else {
        setPredictions(data.predictions || data.games || [])
        setModelStatus(data.model_status || data.modelStatus || null)
      }

      setLastFetch(new Date())
    } catch (err) {
      // Network errors or non-existent endpoint — treat as "not available yet"
      if (err.message?.includes('fetch') || err.name === 'TypeError') {
        setPredictions([])
        setModelStatus(null)
      } else {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPredictions()
    const interval = setInterval(fetchPredictions, REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchPredictions])

  // Model status badge
  const renderModelBadge = () => {
    if (!modelStatus) return null
    const trainedDate = modelStatus.trained_date || modelStatus.trainedDate
    const accuracy = modelStatus.accuracy
    const isStale = modelStatus.stale
    const dotClass = isStale ? 'pred-badge-stale' : ''

    return (
      <div className="pred-model-badge">
        <span className={`pred-model-badge-dot ${dotClass}`} />
        {trainedDate && <span>Trained: <strong>{trainedDate}</strong></span>}
        {accuracy != null && <span>Accuracy: <strong>{(accuracy * 100).toFixed(1)}%</strong></span>}
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="pred-container">
        <div className="pred-loading">
          <div className="pred-loading-spinner" />
          <p>Loading predictions...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="pred-container">
        <div className="pred-error">
          <span className="pred-error-icon">{'\u26A0\uFE0F'}</span>
          <p>Failed to load predictions: {error}</p>
          <button className="pred-retry-btn" onClick={fetchPredictions}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Empty / model training state
  if (!predictions || predictions.length === 0) {
    return (
      <div className="pred-container">
        <div className="pred-header">
          <div className="pred-header-left">
            <span className="pred-icon">{'\u{1F3AF}'}</span>
            <h2>MLB Predictions</h2>
          </div>
        </div>
        <div className="pred-empty">
          <span className="pred-empty-icon">{'\u2699\uFE0F'}</span>
          <span className="pred-empty-title">Model training in progress</span>
          <span className="pred-empty-sub">
            Predictions available soon. The MLB forecasting model is being trained
            on historical data, pitcher matchups, and park factors. Check back shortly.
          </span>
          <div className="pred-training-badge">
            <span className="pred-training-pulse" />
            Training in progress
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pred-container">
      <div className="pred-header">
        <div className="pred-header-left">
          <span className="pred-icon">{'\u{1F3AF}'}</span>
          <h2>MLB Predictions</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {renderModelBadge()}
          {lastFetch && (
            <span className="pred-refresh-info">
              Updated {lastFetch.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="pred-grid">
        {predictions.map((game, idx) => (
          <GameCard key={game.game_id || game.gameId || idx} game={game} />
        ))}
      </div>
    </div>
  )
}
